/**
 * Divisão de despesa — regras puras.
 *
 * Cinco modos, todos redutíveis a "distribuir um total por pesos" mais uma
 * definição de peso:
 *
 * | Modo         | Peso de cada participante            |
 * |--------------|--------------------------------------|
 * | `equal`      | 1                                     |
 * | `per_family` | 1 por família, repartido internamente |
 * | `percentage` | o percentual informado                |
 * | `custom`     | o valor informado (sem distribuição)  |
 * | `by_items`   | a soma dos itens da pessoa            |
 *
 * Três invariantes valem para todos, e são o que os testes verificam
 * exaustivamente:
 *
 *   1. **A soma das partes é exatamente o total.** Sempre, em qualquer modo,
 *      com qualquer arredondamento.
 *   2. **Ninguém recebe parte negativa** quando o total é positivo.
 *   3. **O resultado é determinístico**: mesma entrada, mesma saída, em
 *      qualquer aparelho.
 *
 * Impostos e gorjeta ficam separados do subtotal porque são rateados sobre a
 * participação de cada pessoa no subtotal — quem comeu mais paga mais gorjeta.
 * Somá-los ao total antes de dividir dá o mesmo número no modo `equal`, mas
 * resultado diferente (e errado) em `by_items`.
 */

import { assertMinorAmount, sum } from './money.js';
import { distributeByWeight } from './remainder.js';

export type SplitMode = 'equal' | 'per_family' | 'percentage' | 'custom' | 'by_items';

export interface Participant {
  id: string;
  /** Necessário só no modo `per_family`. */
  familyId?: string | undefined;
}

export interface ExpenseItem {
  id: string;
  /** Inteiro na menor unidade. */
  amount: number;
  /** Quem consome o item. Um item pode ser dividido entre várias pessoas. */
  participantIds: string[];
}

export interface Payment {
  participantId: string;
  amount: number;
}

export interface SplitInput {
  mode: SplitMode;
  /** Subtotal, sem imposto nem gorjeta. Inteiro na menor unidade. */
  subtotal: number;
  participants: readonly Participant[];
  /** Quem pagou e quanto. Um ou vários pagadores. */
  payments: readonly Payment[];
  /** Imposto em valor absoluto, rateado pela participação no subtotal. */
  tax?: number;
  /** Gorjeta em valor absoluto, rateada da mesma forma. */
  tip?: number;
  /** `percentage`: percentual por participante. Precisa somar 100. */
  percentages?: Record<string, number>;
  /** `custom`: valor por participante. Precisa somar o subtotal. */
  amounts?: Record<string, number>;
  /** `by_items`: itens consumidos. A soma precisa dar o subtotal. */
  items?: readonly ExpenseItem[];
}

export interface ParticipantShare {
  participantId: string;
  /** Parte do subtotal. */
  subtotal: number;
  tax: number;
  tip: number;
  /** `subtotal + tax + tip`. É o que a pessoa deve. */
  total: number;
  /** Quanto a pessoa pagou. */
  paid: number;
  /** `paid - total`. Positivo: tem a receber. Negativo: deve. */
  balance: number;
}

export interface SplitResult {
  /** `subtotal + tax + tip`. */
  total: number;
  shares: ParticipantShare[];
}

export class SplitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SplitError';
  }
}

/**
 * Mensagem única da divisão por família sem famílias configuradas.
 *
 * Fica exportada para que servidor e cliente digam exatamente a mesma coisa —
 * duas redações do mesmo erro seriam duas explicações diferentes para a mesma
 * pessoa.
 */
export const FAMILIES_NOT_CONFIGURED =
  'Para dividir por família, primeiro configure as famílias da viagem.';

/**
 * `per_family` pedida sem nenhuma família declarada.
 *
 * Erro próprio, e não um `SplitError` genérico, porque a UI precisa reagir de
 * um jeito específico: desabilitar a modalidade e dizer o que fazer. Um erro
 * genérico viraria "divisão inválida", que não ajuda ninguém.
 */
export class FamiliesNotConfiguredError extends SplitError {
  constructor() {
    super(FAMILIES_NOT_CONFIGURED);
    this.name = 'FamiliesNotConfiguredError';
  }
}

/**
 * Há famílias suficientes para executar a divisão por família?
 *
 * Basta **uma** família declarada. Quem não tem família conta como unidade
 * própria — regra do design, visível na tela, e diferente de degradar em
 * silêncio: ali a pessoa escolheu dividir por família sabendo que existem
 * famílias, e vê quantas são.
 *
 * O caso proibido é o outro: NENHUMA família declarada. Aí `per_family`
 * produziria exatamente o mesmo resultado de `equal`, e a pessoa acharia que
 * dividiu por família quando dividiu por pessoa. É a divisão errada com a
 * aparência da certa.
 */
export function canSplitPerFamily(participants: readonly Participant[]): boolean {
  return participants.some((p) => Boolean(p.familyId));
}

/** Pesos por modo. Todo o resto do cálculo é comum. */
function pesosDe(input: SplitInput): number[] {
  const ids = input.participants.map((p) => p.id);

  switch (input.mode) {
    case 'equal':
      return ids.map(() => 1);

    case 'per_family': {
      // Sem nenhuma família declarada, este modo devolveria o mesmo que
      // `equal` — e devolver silenciosamente seria dar à pessoa a divisão
      // errada com a aparência da certa. Recusa, e diz o que fazer.
      if (!canSplitPerFamily(input.participants)) {
        throw new FamiliesNotConfiguredError();
      }

      // Cada FAMÍLIA carrega uma cota igual, e a cota se reparte entre as
      // pessoas dela. Uma família de 4 não paga quatro vezes o que paga uma
      // pessoa sozinha — é essa a diferença em relação a `equal`, e é a razão
      // de o modo existir.
      const tamanho = new Map<string, number>();
      for (const p of input.participants) {
        const familia = p.familyId ?? p.id;
        tamanho.set(familia, (tamanho.get(familia) ?? 0) + 1);
      }
      return input.participants.map((p) => {
        const familia = p.familyId ?? p.id;
        return 1 / (tamanho.get(familia) ?? 1);
      });
    }

    case 'percentage': {
      const percentuais = input.percentages ?? {};
      const total = sum(ids.map((id) => percentuais[id] ?? 0));
      // Tolerância de ponto flutuante na ENTRADA (o usuário digitou 33,33),
      // nunca na saída — a saída é inteira e fecha exata.
      if (Math.abs(total - 100) > 1e-6) {
        throw new SplitError(`Os percentuais somam ${total}%, e precisam somar 100%.`);
      }
      return ids.map((id) => percentuais[id] ?? 0);
    }

    case 'custom': {
      const valores = input.amounts ?? {};
      for (const id of ids) assertMinorAmount(valores[id] ?? 0, `valor de ${id}`);
      const total = sum(ids.map((id) => valores[id] ?? 0));
      if (total !== input.subtotal) {
        throw new SplitError(
          `Os valores somam ${total} e o subtotal é ${input.subtotal}. Precisam ser iguais.`,
        );
      }
      // Já vêm em inteiros e já fecham: não há o que distribuir.
      return ids.map((id) => valores[id] ?? 0);
    }

    case 'by_items': {
      const itens = input.items ?? [];
      if (itens.length === 0) throw new SplitError('Divisão por itens exige ao menos um item.');

      for (const item of itens) {
        assertMinorAmount(item.amount, `item ${item.id}`);
        if (item.participantIds.length === 0) {
          throw new SplitError(`O item ${item.id} não tem ninguém consumindo.`);
        }
        for (const id of item.participantIds) {
          if (!ids.includes(id)) {
            throw new SplitError(`O item ${item.id} aponta para alguém fora da despesa.`);
          }
        }
      }

      const somaItens = sum(itens.map((i) => i.amount));
      if (somaItens !== input.subtotal) {
        throw new SplitError(
          `Os itens somam ${somaItens} e o subtotal é ${input.subtotal}. Precisam ser iguais.`,
        );
      }

      // Cada item é dividido entre quem o consumiu — com a mesma regra de
      // maior resto, item a item. Somar os itens e só então dividir perderia
      // a informação de quem consumiu o quê.
      const porPessoa = new Map<string, number>(ids.map((id) => [id, 0]));
      for (const item of itens) {
        const partes = distributeByWeight(
          item.amount,
          item.participantIds.map(() => 1),
        );
        item.participantIds.forEach((id, i) => {
          porPessoa.set(id, (porPessoa.get(id) ?? 0) + (partes[i] ?? 0));
        });
      }
      return ids.map((id) => porPessoa.get(id) ?? 0);
    }
  }
}

export function splitExpense(input: SplitInput): SplitResult {
  assertMinorAmount(input.subtotal, 'subtotal');
  const tax = input.tax ?? 0;
  const tip = input.tip ?? 0;
  assertMinorAmount(tax, 'imposto');
  assertMinorAmount(tip, 'gorjeta');

  if (input.participants.length === 0) {
    throw new SplitError('Uma despesa precisa de ao menos um participante.');
  }
  const ids = input.participants.map((p) => p.id);
  if (new Set(ids).size !== ids.length) {
    throw new SplitError('Participante repetido na mesma despesa.');
  }

  const pesos = pesosDe(input);

  // `custom` e `by_items` já produzem inteiros que fecham; distribuir de novo
  // seria correto, mas passar por `distributeByWeight` mantém um caminho só —
  // e o resultado é idêntico, porque os pesos já somam o total.
  const subtotais =
    input.mode === 'custom' || input.mode === 'by_items'
      ? [...pesos]
      : distributeByWeight(input.subtotal, pesos);

  // Imposto e gorjeta seguem a participação de cada um no SUBTOTAL. Quem
  // consumiu mais paga proporcionalmente mais dos dois.
  const pesosDeRateio = subtotais.some((v) => v !== 0) ? subtotais : pesos;
  const impostos = tax === 0 ? ids.map(() => 0) : distributeByWeight(tax, pesosDeRateio);
  const gorjetas = tip === 0 ? ids.map(() => 0) : distributeByWeight(tip, pesosDeRateio);

  const pago = new Map<string, number>(ids.map((id) => [id, 0]));
  for (const pagamento of input.payments) {
    assertMinorAmount(pagamento.amount, 'pagamento');
    if (!pago.has(pagamento.participantId)) {
      throw new SplitError('Quem pagou precisa estar entre os participantes da despesa.');
    }
    pago.set(
      pagamento.participantId,
      (pago.get(pagamento.participantId) ?? 0) + pagamento.amount,
    );
  }

  const total = input.subtotal + tax + tip;
  const totalPago = sum(input.payments.map((p) => p.amount));
  if (input.payments.length > 0 && totalPago !== total) {
    throw new SplitError(
      `Os pagamentos somam ${totalPago} e a despesa é ${total}. Precisam ser iguais.`,
    );
  }

  const shares: ParticipantShare[] = input.participants.map((participante, i) => {
    const subtotal = subtotais[i] ?? 0;
    const imposto = impostos[i] ?? 0;
    const gorjeta = gorjetas[i] ?? 0;
    const devido = subtotal + imposto + gorjeta;
    const pagou = pago.get(participante.id) ?? 0;

    return {
      participantId: participante.id,
      subtotal,
      tax: imposto,
      tip: gorjeta,
      total: devido,
      paid: pagou,
      balance: pagou - devido,
    };
  });

  return { total, shares };
}
